import { FormProvider, useForm, useFormState } from 'react-hook-form';
import { GreenlightRegistrationLookupFields } from '../../../../components/HubPages/components/GreenlightHistoryCheck/GreenlightRegistrationLookupFields';
import { KeyInfoShowMore } from '../../../../components/CarValuationPage/components/KeyInfoShowMore/KeyInfoShowMore';
import { SectionBreakHeader } from '../../../../components/HubPages/components/SectionBreakHeader/SectionBreakHeader';
import { type GreenlightRegistrationLookupProps } from '../../../../components/HubPages/components/GreenlightHistoryCheck/GreenlightRegistrationLookup.typed';
import {
  greenlightRegistrationFieldValues,
  type FormValues,
} from '../../../../components/HubPages/components/GreenlightHistoryCheck/queries/greenlightRegistrationLookupSchema';
import * as Styled from '../../../../components/HubPages/components/GreenlightHistoryCheck/GreenlightRegistrationLookup.styled';
import { HistoryCheckItems } from '../../../../components/HubPages/components/GreenlightHistoryCheck/HistoryCheckItems/HistoryCheckItems';
import { Loading } from '../../../../components/Toolkit/Loading/Loading';
import getConfig from 'next/config';
import { GreenlightPurchaseFooter } from '../../../../components/HubPages/components/GreenlightHistoryCheck/GreenlightPurchaseFooter/GreenlightPurchaseFooter';
import { useGreenlightRegistrationLookup } from './queries/useGreenlightRegistrationLookup';
import { useMapVehicleDataToKeyInfo } from '../../../../components/HubPages/components/GreenlightHistoryCheck/MapVehicleDataToKeyInfo.hook';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUserContext } from '../../../../contexts/UserContext';
import { fireToast } from '../../../../helpers/Toasts';

const defaultValues: FormValues = {
  registration: '',
};

function GreenlightRegistrationLookup({
  ctaText,
  titleImage,
  heroTitle,
  historyCheckItemsTitle,
  historyCheckItems,
}: GreenlightRegistrationLookupProps) {
  const {
    publicRuntimeConfig: { CDN_STATIC_ASSETS },
  } = getConfig();

  const { accessToken } = useUserContext();
  const methods = useForm({
    resolver: zodResolver(greenlightRegistrationFieldValues),
    defaultValues,
  });

  const { isSubmitting, isValid, isDirty } = useFormState<FormValues>({
    control: methods.control,
  });

  const {
    mutate: registrationLookup,
    data,
    isPending,
  } = useGreenlightRegistrationLookup();

  // This API returns a 200 either with registration data or error message
  const vehicleData = data && 'errorMessage' in data ? null : data;

  const isLoading = isSubmitting || isPending;
  const isDisabled = !isValid || isSubmitting || !isDirty || isPending;

  const { vehicleMetaTitle, vehicleMetaList, currentReport, keyInfoListItems } =
    useMapVehicleDataToKeyInfo(vehicleData ?? null);

  const onSubmit = (values: FormValues) => {
    registrationLookup(
      { registration: values.registration, accessToken },
      {
        onSuccess: (data) => {
          // This API can return a 200 with error message, it is simpler to handle this non-ideal flow directly in callback
          // than refactor the global mutationCache error handler to cater for this use-case. Ideally API should be updated.
          // Note that as this does not hit mutationCache error handler, error log is not recorded. This is desired behaviour.
          if ('errorMessage' in data) {
            fireToast({
              text: data.errorMessage,
              type: 'ERROR',
            });
          }
          // Ensures submit button is disabled without subsequent change
          methods.reset(values, { keepValues: true });
        },
      },
    );
  };

  return (
    <>
      <Styled.LookupWrapper>
        <Styled.TextWrapper>
          <Styled.Text>{heroTitle}</Styled.Text>
          {titleImage && (
            <Styled.TitleImg
              alt="Greenlight History Check"
              src={titleImage?.url}
            />
          )}
        </Styled.TextWrapper>
        <Styled.FormWrapper>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
              <Styled.RegistrationLookupContainer>
                <Styled.RegistrationLookupContent>
                  <GreenlightRegistrationLookupFields />
                  <Styled.Button
                    type="submit"
                    data-testid="registration-lookup-submit"
                    disabled={isDisabled}
                  >
                    {isLoading ? (
                      <Loading
                        size="DEFAULT"
                        cdnUrl={CDN_STATIC_ASSETS}
                        color={'LIGHT'}
                      />
                    ) : (
                      ctaText
                    )}
                  </Styled.Button>
                </Styled.RegistrationLookupContent>
              </Styled.RegistrationLookupContainer>
            </form>
          </FormProvider>
        </Styled.FormWrapper>
      </Styled.LookupWrapper>

      {vehicleData ? (
        <Styled.SectionWrapper>
          <SectionBreakHeader title="Your Vehicle Details" />
          <KeyInfoShowMore
            infoList={vehicleMetaList}
            keyInfoTitle={vehicleMetaTitle}
            keyInfoListItems={keyInfoListItems}
          />
        </Styled.SectionWrapper>
      ) : (
        <Styled.SectionWrapper data-testid={'history-check-items'}>
          <HistoryCheckItems
            items={historyCheckItems}
            title={historyCheckItemsTitle?.text}
          />
        </Styled.SectionWrapper>
      )}
      {currentReport && (
        <GreenlightPurchaseFooter
          currentReport={currentReport}
          reportText={'Get full report for '}
          registration={vehicleData?.registration || ''}
        />
      )}
    </>
  );
}

export { GreenlightRegistrationLookup };
